// HOOKS
import { useContext, useEffect } from "react";
import useColumns from "../../../hooks/other/useColumns.other.hook";
import useMultiSelectStore from "../../../hooks/other/useMultiSelectStore.other.hook";
import useSelectAll from "../../../hooks/other/useSelectAll.other.hook";
// CONTEXTS
import { SelectedOpenPartOrdersContext } from "../../../contexts/parts/selectedOpenPartOrders.part.context";
import { OpenPartOrdersContext } from "../../../contexts/parts/openPartOrders.context";
// COMPONENTS
import MultiOptionFilter from "../../../components/tables/customizableTable/features/multiOptionFilter/multiOptionFilter";
import Refresh from "../../../components/refresh/refresh.component";
import Spinner from "../../../components/spinner/spinner.component";
import Table from "../../../components/tables/customizableTable/table.customizableTable";
import PartsRow from "../../../components/tables/parts/open/row.open.parts.table.component";
import ResizableHeader from "../../../components/tables/customizableTable/headers/resizable.header";
import OpenPartOrdersInfoBar from "../../../components/infoBar/parts/openPartOrders.infoBar.component";
import OpenPartOrdersActionBar from "../../../components/actionBar/parts/openPartOrders.actionBar.component";
import CustomizableSearch from "../../../components/tables/customizableTable/features/search/search";
import PaginationBar from "../../../components/tables/customizableTable/features/pagination/paginationBar";
import LimitDropdown from "../../../components/tables/customizableTable/features/limit/limit.dropdown";
import ColumnFilter from "../../../components/tables/customizableTable/features/columnFilter/columnFilter";
// CONSTANTS
import DEFAULT_OPEN_ORDERS_COLUMNS from "../../../constants/parts/defaultOpenOrdersColumns";
import OPEN_PART_STATUSES from "../../../constants/parts/openPartStatuses.part.constant";

const CLASSNAMES = {
  pagination: {
    arrow:
      "active:shadow-inner active:shadow-black bg-lightPrimary hover:bg-lightSecondary shadow shadow-black hover:cursor-pointer hover:dark:bg-darkSecondary dark:bg-darkPrimary dark:fill-white rounded-md w-8 h-8",
    currentPage:
      "shadow-inner shadow-black dark:bg-darkSecondary bg-lightSecondary dark:text-white text-white hover:cursor-pointer rounded h-8 w-8 flex items-center justify-center",
    page: "active:shadow-inner active:shadow-black shadow shadow-black hover:dark:bg-darkSecondary hover:bg-lightSecondary active:shadow-inner dark:text-white text-white dark:bg-darkPrimary bg-lightPrimary hover:cursor-pointer rounded h-8 w-8 flex items-center justify-center",
  },
  columnFilter: {
    dropdownButton: {
      dropdown:
        "border-animate rounded h-[250px] w-[250px] overflow-y-scroll absolute bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
  limit: {
    dropdownButton: {
      dropdown:
        "border-animate rounded absolute bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
  spinner: {
    container: "flex w-full h-full justify-center items-center",
    spinner:
      "shadow animate-spin border-dashed rounded-full h-16 w-16 border-2 border-primary",
  },
};

const filterHiddenColumns = (column) => column?.show;

const OpenPartsPage = () => {
  const { selectAll, handleSelectAll, resetSelectAll } = useSelectAll();
  const {
    options: statusFilterOptions,
    handleChange: handleStatusFilterChange,
  } = useMultiSelectStore({ defaultOptions: OPEN_PART_STATUSES });
  const { columns, setColumns, handleColumnFilterCheck, handleResize } =
    useColumns();

  useEffect(() => {
    setColumns(DEFAULT_OPEN_ORDERS_COLUMNS);
  }, []);
  const {
    metaData,
    partOrders,
    query,
    partOrderInput,
    fetchPartOrders,
    setPartOrderInput,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
    isLoading,
  } = useContext(OpenPartOrdersContext);
  const { setSelectedOpenPartOrders, selectedOpenPartOrders } = useContext(
    SelectedOpenPartOrdersContext
  );
  const { limit, page, search, sort } = query;

  // Set and fetch statuses to filter by
  useEffect(() => {
    setPartOrderInput({
      ...partOrderInput,
      status: statusFilterOptions
        .filter((option) => option.checked)
        .map((option) => option.value),
    });
  }, [statusFilterOptions]);

  // Reset selectAll when selected parts is empty
  useEffect(() => {
    if (selectedOpenPartOrders.length === 0) return resetSelectAll();
  }, [selectedOpenPartOrders]);
  // Reset selectAll when page changes
  useEffect(() => {
    resetSelectAll();
  }, [page]);

  // Add/Remove orderedParts from selectedOrderedParts
  const selectAllHandler = () => {
    const updatedSelectedOpenParts = handleSelectAll(
      selectedOpenPartOrders,
      partOrders,
      "_id"
    );
    setSelectedOpenPartOrders(updatedSelectedOpenParts);
  };
  return (
    <div
      style={{ height: "calc(100vh - 136px)" }}
      className="p-2 flex flex-col gap-2 overflow-y-hidden"
    >
      <div className="flex flex-row justify-between">
        <div className="p-2 flex flex-row gap-2 bg-slate-100 border border-slate-200">
          <CustomizableSearch search={search} onSearch={handleSearch} />
          <LimitDropdown
            handleClick={handleLimitChange}
            classNames={CLASSNAMES.limit}
            limit={limit}
          />
          <ColumnFilter
            classNames={CLASSNAMES.columnFilter}
            columns={columns}
            handleChange={handleColumnFilterCheck}
          />
          <MultiOptionFilter
            options={statusFilterOptions}
            handleChange={handleStatusFilterChange}
            dropdownButtonProps={{
              label: "Status",
              value:
                statusFilterOptions.filter((option) => option.checked)
                  ?.length || 0,
              classNames: {
                dropdown:
                  "w-[225px] border-animate rounded overflow-y-auto absolute bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
              },
            }}
          />
        </div>
        <OpenPartOrdersActionBar />
      </div>
      <div className="p-2">
        <PaginationBar
          classNames={CLASSNAMES.pagination}
          currentPage={page}
          totalPages={metaData.totalPages}
          handleChange={handlePageChange}
        />
      </div>
      <div className="flex flex-row justify-between">
        <OpenPartOrdersInfoBar totalCount={metaData?.totalCount} />
        <Refresh minimumValue={30} handleEvent={fetchPartOrders} />
      </div>
      <div className="overflow-x-auto h-full border bg-slate-100 rounded px-1 pb-8">
        {isLoading ? (
          <Spinner classNames={CLASSNAMES.spinner} />
        ) : (
          <Table
            header={
              <tr>
                <th className="max-w-[45px]">
                  <input
                    className="checked:accent-primary w-[45px]"
                    type="checkbox"
                    onChange={selectAllHandler}
                    checked={selectAll}
                  />
                </th>
                {columns?.filter(filterHiddenColumns)?.map((column) => (
                  <ResizableHeader
                    sortProps={{ sort, handleSort }}
                    key={column?.id}
                    column={column}
                    handleResize={handleResize}
                  >
                    {column?.label}
                  </ResizableHeader>
                ))}
              </tr>
            }
            body={partOrders.map((partOrder, index) =>
              partOrder?._id ? (
                <PartsRow
                  handleSearch={handleSearch}
                  key={partOrder._id}
                  columns={columns.filter((column) => column?.show)}
                  select={true}
                  data={partOrder}
                  className={`h-[50px] ${
                    index % 2 === 0 ? "bg-slate-100" : "bg-gray-300"
                  }`}
                />
              ) : null
            )}
          />
        )}
      </div>
    </div>
  );
};

export default OpenPartsPage;
